/**
 * Active-clinic store for multi-clinic owners.
 *
 * Deliberately a plain module, not React state: `api.client.ts` must read the
 * value synchronously on every request and cannot consume a context. React
 * state lives in ClinicContext and mirrors into here.
 *
 * ## This value is a request, never a grant
 *
 * Whatever is stored here goes out as the X-Active-Clinic header, and the
 * backend re-validates it against the caller's org membership on EVERY request
 * before it reaches RLS. A tampered localStorage entry gets a 403, not another
 * clinic's data. Treat it as a UI preference with no security weight.
 *
 * Persisted so a refresh keeps the owner in the branch they were working in —
 * losing it would silently drop them back to their home clinic mid-task, which
 * reads as data loss even though nothing was lost.
 */

const STORAGE_KEY = "kaagazy_active_clinic_id";

// Rewritten by whichever tab changes the clinic this browser works in.
const SCOPE_EPOCH_KEY = "kaagazy_clinic_scope_epoch";

export const CLINIC_SCOPE_CHANGED_EVENT = "kaagazy:clinic-scope-changed";

// Mirrors localStorage. Kept in memory because api.client reads it on every
// request and localStorage access is synchronous but not free.
let _activeClinicId: string | null = readFromStorage(STORAGE_KEY);

// The epoch this tab loaded under. It never re-syncs from storage: a tab whose
// epoch no longer matches is showing a clinic the browser has left, and only a
// reload may bring it back.
let _scopeEpoch: string | null = readFromStorage(SCOPE_EPOCH_KEY);

function readFromStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode / blocked site data — fall back to memory-only for this tab.
    return null;
  }
}

export function getActiveClinicId(): string | null {
  return _activeClinicId;
}

export function setActiveClinicId(clinicId: string | null): void {
  _activeClinicId = clinicId;
  try {
    if (clinicId) localStorage.setItem(STORAGE_KEY, clinicId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* memory-only for this tab; the header still goes out correctly */
  }
}

// ─── Home clinic ─────────────────────────────────────────────────────────────
//
// The clinic on the user's own row. With no header the backend already scopes
// every request to it, so naming it adds nothing — and a header the prod API
// gateway's CORS allow-list does not know yet would block the whole request.
// Remembered across reloads so the org-context call itself, the first request
// a reload makes, can already leave the header off.

const HOME_STORAGE_KEY = "kaagazy_home_clinic_id";

let _homeClinicId: string | null = readFromStorage(HOME_STORAGE_KEY);

export function setHomeClinicId(clinicId: string | null): void {
  _homeClinicId = clinicId;
  try {
    if (clinicId) localStorage.setItem(HOME_STORAGE_KEY, clinicId);
    else localStorage.removeItem(HOME_STORAGE_KEY);
  } catch {
    /* memory-only for this tab */
  }
}

/**
 * The X-Active-Clinic value to send, or null to send none. Null unless the
 * user is working in a clinic other than their home one, so ordinary staff —
 * and an owner in their home clinic — never send the header at all.
 */
export function getActiveClinicHeader(): string | null {
  if (!_activeClinicId || _activeClinicId === _homeClinicId) return null;
  return _activeClinicId;
}

/**
 * Drop the stored selection. Called on signOut, and on a 403
 * `active_clinic_invalid` — the backend has told us this clinic is no longer
 * reachable (moved out of the org, deactivated), so the stale id must go
 * rather than 403 every subsequent request.
 */
export function clearActiveClinicId(): void {
  setActiveClinicId(null);
}

// ─── One clinic per browser, across tabs ─────────────────────────────────────
//
// Every tab of this browser shares one IndexedDB cache and one offline queue,
// so the browser can only ever work in one clinic. When a tab switches, every
// other open tab is left rendering the old clinic while the shared cache fills
// with the new one — and its pull, push and form saves would then mix the two.
// So a switching tab announces it, and every other tab locks until reloaded.

export class ClinicScopeChangedError extends Error {
  constructor(message = "Another tab switched this browser to a different clinic.") {
    super(message);
    this.name = "ClinicScopeChangedError";
  }
}

/**
 * Called by the tab that is about to change this browser's clinic, before it
 * touches the shared queue or cache. Pass null when the target is unknown.
 */
export function announceClinicScopeChange(toClinicId: string | null): void {
  const epoch = JSON.stringify({
    to: toClinicId,
    nonce: `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`,
  });
  _scopeEpoch = epoch;
  try {
    localStorage.setItem(SCOPE_EPOCH_KEY, epoch);
  } catch {
    /* storage blocked: tabs cannot share a cache either, nothing to announce */
  }
}

/** True once another tab has changed this browser's clinic. */
export function isClinicScopeStale(): boolean {
  let current: string | null;
  try {
    current = localStorage.getItem(SCOPE_EPOCH_KEY);
  } catch {
    return false;
  }
  return current !== _scopeEpoch;
}

/** The clinic the other tab moved this browser to, when it said. */
export function getClinicScopeTarget(): string | null {
  try {
    const raw = localStorage.getItem(SCOPE_EPOCH_KEY);
    const to = raw ? JSON.parse(raw)?.to : null;
    return typeof to === "string" ? to : null;
  } catch {
    return null;
  }
}

/**
 * Refuse to act for a tab that is out of date. Every request to our API and
 * every write the sync engines make goes through this first, so a stale tab
 * can neither send its old clinic's header nor write into the shared cache.
 */
export function assertClinicScopeCurrent(): void {
  if (!isClinicScopeStale()) return;
  window.dispatchEvent(new CustomEvent(CLINIC_SCOPE_CHANGED_EVENT));
  throw new ClinicScopeChangedError();
}

/**
 * This tab's clinic scope. A sync run takes it before its first request and
 * hands it to `assertClinicScopeUnchanged` before every write, so a run that
 * began under one clinic cannot finish writing under another.
 */
export function getClinicScopeToken(): string | null {
  return _scopeEpoch;
}

/** Throws if any tab, including this one, changed clinics since `token`. */
export function assertClinicScopeUnchanged(token: string | null): void {
  assertClinicScopeCurrent();
  if (token !== _scopeEpoch) {
    throw new ClinicScopeChangedError("The clinic changed while this sync was running.");
  }
}

/** Runs `onStale` when another tab changes the clinic. Returns an unsubscribe. */
export function onClinicScopeChangedElsewhere(onStale: () => void): () => void {
  const check = () => {
    if (isClinicScopeStale()) onStale();
  };
  // key === null is another tab calling localStorage.clear().
  const onStorage = (e: StorageEvent) => {
    if (e.key === SCOPE_EPOCH_KEY || e.key === null) check();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(CLINIC_SCOPE_CHANGED_EVENT, check);
  // A frozen background tab may resume before its storage event is delivered.
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(CLINIC_SCOPE_CHANGED_EVENT, check);
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", check);
  };
}
